/**
 * Toast — lightweight, self-contained transient notification.
 *
 * A single `showToast(message, opts)` entry point any module can call to flash a
 * non-blocking message near the top of the screen (e.g. "Couldn't join that
 * room", "Connection lost"). Toasts stack, auto-dismiss, and clear on tap. Pure
 * DOM + injected CSS, no dependencies. z-index 600 — below the chat bubble (700)
 * and coach marks (760), above normal UI. Sits clear of the top header bar.
 */

export type ToastType = 'error' | 'info' | 'success';

export interface ToastOptions {
  /** Accent + intent. Defaults to 'info'. */
  type?: ToastType;
  /** Auto-dismiss delay in ms. Defaults to 4000. */
  durationMs?: number;
}

const STYLE_ID = 'sdl-toast-styles';
const CONTAINER_ID = 'sdl-toast-container';

const ACCENT: Record<ToastType, string> = {
  error: '#FF3366',
  info: '#00E5FF',
  success: '#00FF88',
};

const ICON: Record<ToastType, string> = {
  error: '!',
  info: 'i',
  success: '✓',
};

function ensureContainer(): HTMLElement {
  injectStyles();
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    document.body.appendChild(container);
  }
  return container;
}

/** Flash a transient toast. Safe to call from anywhere, any number of times. */
export function showToast(message: string, opts: ToastOptions = {}): void {
  const type = opts.type ?? 'info';
  const duration = opts.durationMs ?? 4000;
  const container = ensureContainer();

  const toast = document.createElement('div');
  toast.className = `sdl-toast sdl-toast-${type}`;
  toast.style.setProperty('--toast-accent', ACCENT[type]);

  const icon = document.createElement('span');
  icon.className = 'sdl-toast-icon';
  icon.textContent = ICON[type];

  const text = document.createElement('span');
  text.className = 'sdl-toast-text';
  text.textContent = message;

  toast.append(icon, text);
  container.appendChild(toast);

  // Next frame → trigger the slide/fade-in transition.
  requestAnimationFrame(() => toast.classList.add('visible'));

  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    toast.classList.remove('visible');
    // Remove after the fade-out; a timeout backstops a missed transitionend.
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    window.setTimeout(() => toast.remove(), 400);
  };

  const timer = window.setTimeout(dismiss, duration);
  toast.addEventListener('click', dismiss);
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${CONTAINER_ID} {
      position: fixed;
      /* Sit clear of the top header/back-button row. */
      top: calc(env(safe-area-inset-top, 0px) + 62px);
      left: 50%;
      transform: translateX(-50%);
      z-index: 600;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      pointer-events: none;
      width: max-content;
      max-width: min(92vw, 440px);
    }

    .sdl-toast {
      pointer-events: auto;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 11px;
      max-width: 100%;
      padding: 13px 18px;
      border-radius: 14px;
      /* Near-opaque so the busy lobby/scene behind never bleeds through the
         text; blur adds extra separation where supported. */
      background: rgba(18, 18, 24, 0.98);
      -webkit-backdrop-filter: blur(14px);
      backdrop-filter: blur(14px);
      border: 1.5px solid var(--toast-accent, #00E5FF);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
      color: #fff;
      font-family: 'Rajdhani', sans-serif;
      font-size: 15.5px;
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 0.3px;
      opacity: 0;
      transform: translateY(-12px) scale(0.96);
      transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .sdl-toast.visible {
      opacity: 1;
      transform: translateY(0) scale(1);
    }

    .sdl-toast-icon {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--toast-accent, #00E5FF);
      color: #0a0a0e;
      font-size: 14px;
      font-weight: 800;
      font-style: normal;
      line-height: 22px;
      text-align: center;
    }

    .sdl-toast-text {
      flex: 1 1 auto;
      text-align: left;
    }
  `;
  document.head.appendChild(style);
}
